import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';

import { getComparator, Order, getAllDocuments } from './tableUtills';
import {
  useGetDocumentTree,
  useMutateGetDocument,
} from '../../api/documentHub';
import { DocumentTableRow, DocumentTypes } from '../../models/documentHub';

import { TableMenu } from './TableMenu';
import { useEffect, useState, useMemo } from 'react';
import { Snackbar, Alert, Typography } from '@mui/material';
interface HeadCell {
  disablePadding: boolean;
  id: keyof DocumentTableRow;
  label: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'documentName',
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'createdOn',
    disablePadding: false,
    label: 'Date/Time Last Updated',
  },
  {
    id: 'uploadedBy',
    disablePadding: false,
    label: 'Uploaded By',
  },
  {
    id: 'folderName',
    disablePadding: false,
    label: 'Folder Name',
  },
  {
    id: 'actionButton',
    disablePadding: false,
    label: '',
    align: 'center',
    sortable: false,
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof DocumentTableRow
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof DocumentTableRow) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable == undefined || headCell.sortable ? (
              <TableSortLabel
                key={`${headCell.id}-sort-label`}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              <></>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function DocumentHubTableDocuments(props: { folderType: string }) {
  const { folderType } = props;
  const { data: folders } = useGetDocumentTree();

  const [documents, setDocuments] = useState<DocumentTableRow[]>([]);

  useEffect(() => {
    if (folders) {
      setDocuments(getAllDocuments(folders, folderType));
    }
  }, [folderType, folders]);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] =
    useState<keyof DocumentTableRow>('documentName');

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof DocumentTableRow
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getMutation = useMutateGetDocument();
  const handleClick = (
    _event: React.MouseEvent<unknown>,
    row: DocumentTableRow
  ) => {
    if (!getMutation.isPending) {
      getMutation.mutate(
        { name: row.documentName || '', type: row.folderKey as DocumentTypes },
        {
          onSuccess: (data) => {
            if (data && data.preSignedUrl) {
              window.open(data.preSignedUrl, '_blank');
            }
          },
        }
      );
    }
  };

  const sortedDocuments = useMemo(() => {
    return [...documents].sort(getComparator(order, orderBy));
  }, [documents, order, orderBy]);

  return (
    <>
      {documents && documents.length > 0 ? (
        <Box
          sx={{ width: '100%', height: '100%' }}
          className="document-hub-table"
        >
          <Paper
            sx={{
              width: '100%',
              height: '100%',
              mb: 2,
              boxShadow: 'unset',
            }}
          >
            <TableContainer
              sx={{
                border: 'solid 1px #EAECF0',
                borderBottom: '0',
              }}
            >
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={'medium'}
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={documents.length}
                />
                <TableBody>
                  {sortedDocuments.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        key={`${row.documentName}-key`}
                        sx={{ cursor: 'pointer' }}
                        onClick={(event) => handleClick(event, row)}
                      >
                        <TableCell
                          key={`${row.documentName}-${index}`}
                          align="left"
                        >
                          {row.documentName}
                        </TableCell>
                        <TableCell
                          key={`${row.createdOn}-${index}`}
                          align="left"
                        >
                          {row.createdOn}
                        </TableCell>
                        <TableCell
                          key={`${row.uploadedBy}-${index}`}
                          align="left"
                        >
                          {row.uploadedBy}
                        </TableCell>
                        <TableCell
                          key={`${row.folderName}-${index}`}
                          align="left"
                        >
                          {row.folderName}
                        </TableCell>
                        <TableMenu row={row} />
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={getMutation.isPending}
          >
            <Alert severity="info">Loading Document... </Alert>
          </Snackbar>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={getMutation.isError}
          >
            <Alert severity="error">
              Something went wrong. Please try again.
            </Alert>
          </Snackbar>
        </Box>
      ) : (
        <Box className="no-results">
          <Typography className="no-results-text">
            No documents found
          </Typography>
        </Box>
      )}
    </>
  );
}
